import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {IssueCreationKind} from '../utils/model'
import type {IssueCreateContext} from '../utils/option-config'

const ANALYTICS_TARGET = 'DUPLICATE_DETECTION_ISSUE_CREATION'

export const DuplicateDetectionAnalyticsEvent = {
  RequestComplete: 'issue_create.duplicate_detection.request_complete',
  IssueClicked: 'issue_create.duplicate_detection.issue_clicked',
  IssueCreated: 'issue_create.duplicate_detection.issue_created',
  Dismissed: 'issue_create.duplicate_detection.dismissed',
} as const

type IssueCreatedPayload = {
  issueId: string
  issueNumber: number
  hadDuplicateMatches: boolean
  wasIssueClicked: boolean
  issueClickCount: number
  normalizedBodyLength: number
  titleLength: number
  bodyLength: number
}

type UseDuplicateDetectionAnalyticsOptions = {
  context: IssueCreateContext
  repoNWO: string
  issueKind: IssueCreationKind | undefined
  initialTemplateLength: number
  addedThresholdLength: number
}

/**
 * Hook for sending duplicate detection analytics events.
 * Session ID is passed at call time to ensure callbacks are stable and always use the current value.
 */
export function useDuplicateDetectionAnalytics({
  context,
  repoNWO,
  issueKind,
  initialTemplateLength,
  addedThresholdLength,
}: UseDuplicateDetectionAnalyticsOptions) {
  const {sendAnalyticsEvent} = useAnalytics()

  /** Sends an analytics event with the session ID and context. Additional metadata can be provided. */
  const sendDuplicateDetectionEvent = useCallback(
    (event: string, sessionId: string, extra?: Record<string, unknown>) => {
      sendAnalyticsEvent(event, ANALYTICS_TARGET, {
        issueCreateSessionId: sessionId,
        issueCreateContext: context,
        repoNWO,
        issueKind,
        initialTemplateLength,
        addedThresholdLength,
        ...extra,
      })
    },
    [sendAnalyticsEvent, context, repoNWO, issueKind, initialTemplateLength, addedThresholdLength],
  )

  /** Tracks when a duplicate detection request completes successfully. */
  const sendRequestComplete = useCallback(
    (sessionId: string, resultCount: number, titleLength: number, bodyLength: number) =>
      sendDuplicateDetectionEvent(DuplicateDetectionAnalyticsEvent.RequestComplete, sessionId, {
        resultCount,
        titleLength,
        bodyLength,
      }),
    [sendDuplicateDetectionEvent],
  )

  /** Tracks whether users interact with results, along with position of the clicked item. */
  const sendIssueClicked = useCallback(
    (sessionId: string, position: number) =>
      sendDuplicateDetectionEvent(DuplicateDetectionAnalyticsEvent.IssueClicked, sessionId, {position}),
    [sendDuplicateDetectionEvent],
  )

  /** Tracks when the user dismisses duplicate detection results. */
  const sendDismissed = useCallback(
    (sessionId: string, dismissedItemCount: number) =>
      sendDuplicateDetectionEvent(DuplicateDetectionAnalyticsEvent.Dismissed, sessionId, {dismissedItemCount}),
    [sendDuplicateDetectionEvent],
  )

  /** Tracks when an issue is created, with analysis of duplicate detection interactions. */
  const sendIssueCreated = useCallback(
    (sessionId: string, payload: IssueCreatedPayload) =>
      sendDuplicateDetectionEvent(DuplicateDetectionAnalyticsEvent.IssueCreated, sessionId, {
        issueId: payload.issueId,
        issueNumber: payload.issueNumber,
        hadDuplicateMatches: payload.hadDuplicateMatches,
        wasIssueClicked: payload.wasIssueClicked,
        issueClickCount: payload.issueClickCount,
        titleLength: payload.titleLength,
        bodyLength: payload.bodyLength,
        normalizedBodyLength: payload.normalizedBodyLength,
      }),
    [sendDuplicateDetectionEvent],
  )

  return {
    sendRequestComplete,
    sendIssueClicked,
    sendIssueCreated,
    sendDismissed,
  }
}
